/**
 * The drop target — a receiver interface state, not decoration.
 *
 * The visitor feeds a signal into the receiver; the receiver does not load a file
 * (ENG-077). Copy is a UI affordance by owner decision, outside receiver dialogue.
 */
import type { Ctx } from '../core/machine';

const ACCEPT = ['audio/'];
const EXT = ['.wav', '.mp3', '.flac', '.ogg', '.m4a', '.aac', '.aif', '.aiff'];

export const isAudio = (f: File): boolean =>
  ACCEPT.some((t) => f.type.startsWith(t)) ||
  EXT.some((e) => f.name.toLowerCase().endsWith(e));

export function mountDropTarget(
  parent: HTMLElement,
  onFile: (f: File) => void
): (c: Ctx, live: boolean) => void {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'cc-drop';
  el.textContent = 'DROP AUDIO HERE';
  // Keyboard and screen-reader path — never colour or drag alone (ENG-015).
  el.setAttribute('aria-label', 'Drop an audio file onto the receiver, or activate to choose a file');
  parent.append(el);

  const picker = document.createElement('input');
  picker.type = 'file';
  picker.accept = '.wav,.mp3,.flac,.ogg,.m4a,.aac,.aif,.aiff,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav,audio/flac,audio/x-flac,audio/ogg,audio/aac,audio/aiff,audio/x-aiff';
  picker.className = 'cc-visually-hidden';
  // The visible drop button is the keyboard-operated control. Visual clipping
  // does not remove this backing input from sequential focus navigation.
  picker.tabIndex = -1;
  parent.append(picker);

  el.addEventListener('click', () => picker.click());
  picker.addEventListener('change', () => {
    const f = picker.files?.[0];
    if (f) onFile(f);
  });

  const stop = (e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); };
  const zone = parent;
  zone.addEventListener('dragover', (e) => { stop(e); zone.classList.add('is-over'); });
  zone.addEventListener('dragleave', (e) => { stop(e); zone.classList.remove('is-over'); });
  zone.addEventListener('drop', (e) => {
    stop(e);
    zone.classList.remove('is-over');
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  });

  // No control appears live before it is (ENG-037). Liveness comes from the stage
  // table, never from a condition duplicated here.
  return (_c: Ctx, live: boolean): void => {
    el.hidden = !live;
    el.disabled = !live;
  };
}
